import React, { useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { BrandProvider } from './context/BrandContext';
import { Toaster } from 'react-hot-toast';
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import ForgotPassword from './components/auth/ForgotPassword';
import ResetPassword from './components/auth/ResetPassword';
import SuperAdminLogin from './components/auth/SuperAdminLogin';
import Layout from './components/layout/Layout';
import Dashboard from './components/dashboard/Dashboard';
import AccountList from './components/accounts/AccountList';
import JournalList from './components/journal/JournalList';
import JournalEntry from './components/journal/JournalEntry';
import CustomerList from './components/customers/CustomerList';
import CustomerForm from './components/customers/CustomerForm';
import VendorList from './components/vendors/VendorList';
import VendorForm from './components/vendors/VendorForm';
import InvoiceList from './components/invoices/InvoiceList';
import InvoiceForm from './components/invoices/InvoiceForm';
import InvoiceEditor from './components/invoices/InvoiceEditor';
import InvoiceView from './components/invoices/InvoiceView';
import BillList from './components/bills/BillList';
import BillForm from './components/bills/BillForm';
import PaymentList from './components/payments/PaymentList';
import PaymentForm from './components/payments/PaymentForm';
import PaymentView from './components/payments/PaymentView';
import BankAccounts from './components/banking/BankAccounts';
import BankTransactions from './components/banking/BankTransactions';
import BankReconciliation from './components/banking/BankReconciliation';
import WriteCheck from './components/banking/WriteCheck';
import RecordDeposit from './components/banking/RecordDeposit';
import ExpenseList from './components/expenses/ExpenseList';
import ExpenseForm from './components/expenses/ExpenseForm';
import ProductList from './components/products/ProductList';
import ProductForm from './components/products/ProductForm';
import InventoryList from './components/inventory/InventoryList';
import InventoryAdjust from './components/inventory/InventoryAdjust';
import AssetList from './components/assets/AssetList';
import AssetForm from './components/assets/AssetForm';
import ReportCenter from './components/reports/ReportCenter';
import TaxSettings from './components/tax/TaxSettings';
import FiscalManagement from './components/settings/FiscalManagement';
import AuditTrail from './components/settings/AuditTrail';
import UserManagement from './components/settings/UserManagement';
import CompanySettings from './components/settings/CompanySettings';
import CurrencySettings from './components/settings/CurrencySettings';
import ThemeBrandingSettings from './components/settings/ThemeBrandingSettings';
import ProfileSettings from './components/settings/ProfileSettings';
import BackupManager from './components/settings/BackupManager';
import Estimates from './components/estimates/Estimates';
import EstimateForm from './components/estimates/EstimateForm';
import SalesOrders from './components/salesOrders/SalesOrders';
import SalesOrderForm from './components/salesOrders/SalesOrderForm';
import PurchaseOrders from './components/purchaseOrders/PurchaseOrders';
import PurchaseOrderForm from './components/purchaseOrders/PurchaseOrderForm';
import Employees from './components/employees/Employees';
import EmployeeForm from './components/employees/EmployeeForm';
import Payroll from './components/payroll/Payroll';
import PaySlip from './components/employees/PaySlip';
import Documents from './components/documents/Documents';
import Applications from './components/applications/Applications';
import Services from './components/services/Services';
import Notifications from './components/notifications/Notifications';
import RecurringSchedules from './components/recurring/RecurringSchedules';
import SubscriptionPage from './components/subscription/SubscriptionPage';
import SubscriptionRenewalPage from './components/subscription/SubscriptionRenewalPage';
import SaaSDashboard from './components/saas/SaaSDashboard';
import SuperAdminRoute from './components/superAdmin/SuperAdminRoute';
import SuperAdminLayout from './components/superAdmin/SuperAdminLayout';
import SuperAdminDashboard from './components/superAdmin/SuperAdminDashboard';
import SuperAdminCompanies from './components/superAdmin/SuperAdminCompanies';
import SuperAdminCompanyDetail from './components/superAdmin/SuperAdminCompanyDetail';
import SuperAdminSubscriptions from './components/superAdmin/SuperAdminSubscriptions';
import SuperAdminPlans from './components/superAdmin/SuperAdminPlans';
import SuperAdminPayments from './components/superAdmin/SuperAdminPayments';
import SuperAdminUsers from './components/superAdmin/SuperAdminUsers';
import SuperAdminNotifications from './components/superAdmin/SuperAdminNotifications';
import SuperAdminActivity from './components/superAdmin/SuperAdminActivity';
import SuperAdminSupport from './components/superAdmin/SuperAdminSupport';
import SuperAdminSettings from './components/superAdmin/SuperAdminSettings';
import SuperAdminReports from './components/superAdmin/SuperAdminReports';
import SuperAdminPaymentPage from './components/superAdmin/SuperAdminPaymentPage';
import SuperAdminInvoicePage from './components/superAdmin/SuperAdminInvoicePage';
import SuperAdminLegal from './components/superAdmin/SuperAdminLegal';
import SuperAdminChat from './components/superAdmin/SuperAdminChat';
import LegalPage from './components/legal/LegalPage';
import ContactPage from './components/legal/ContactPage';
import PlansPage from './components/plans/PlansPage';
import LandingPage from './components/landing/LandingPage';
import SuperAdminLandingPage from './components/superAdmin/SuperAdminLandingPage';
import './App.css';
import './styles/desktop.css';
import './styles/saas.css';
import './styles/invoice-editor.css';
import './styles/document-template.css';
import './styles/auth.css';
import FaviconLoader from './components/common/FaviconLoader';

const ACCESS_MESSAGES = {
  no_subscription: 'No subscription found. Please choose a plan to continue using LedgerPro.',
  trial_expired: 'Your 14-day free trial has expired. Please purchase a plan to continue using LedgerPro.',
};

const PrivateRoute = ({ children }) => {
  const { user, loading, logout, getAccessState, refreshUser } = useAuth();
  const access = user && !loading ? getAccessState() : 'ok';
  const locked = access !== 'ok';
  // One server-side verification per lock episode — prevents both spurious
  // logouts (stale local subscription) and infinite verification loops.
  const verifyAttempted = useRef(false);

  useEffect(() => {
    if (!user || loading) return;
    if (access === 'ok') { verifyAttempted.current = false; return; }
    if (!verifyAttempted.current) {
      verifyAttempted.current = true;
      // Locally cached subscription may be stale/null — confirm with the
      // server before ending the session.
      refreshUser().catch(() => {
        sessionStorage.setItem('ledgerpro_lock_reason', ACCESS_MESSAGES[access] || 'Your subscription is inactive.');
        logout();
      });
    } else {
      // Already re-verified and still locked → genuine lockout
      sessionStorage.setItem('ledgerpro_lock_reason', ACCESS_MESSAGES[access] || 'Your subscription is inactive.');
      logout();
    }
  }, [user, loading, access]);

  if (loading) return <div className="loading-screen"><div className="spinner" /><p>Loading LedgerPro...</p></div>;
  if (!user) return <Navigate to="/login" />;
  if (locked) return <div className="loading-screen"><div className="spinner" /><p>Verifying your subscription...</p></div>;
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading, impersonating } = useAuth();
  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  if (!user) return children;
  // If impersonating, always go to company dashboard
  if (impersonating) return <Navigate to="/dashboard" />;
  // Company user → /dashboard  |  Platform Super Admin → /super-admin
  const isPlatformAdmin = !user.company_id && (user.id === 1 || ['Administrator', 'Superadmin'].includes(user.role_name));
  return <Navigate to={isPlatformAdmin ? '/super-admin' : '/dashboard'} />;
};

/** Redirect platform super admins away from company accounting pages */
const AdminRedirect = ({ children }) => {
  const { user, loading, impersonating } = useAuth();
  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  // If impersonating, allow access to company dashboard
  if (impersonating) return children;
  const isPlatformAdmin = !user?.company_id && (user?.id === 1 || ['Administrator', 'Superadmin'].includes(user?.role_name));
  if (isPlatformAdmin) return <Navigate to="/super-admin" />;
  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrandProvider>
      <FaviconLoader />
      <Toaster position="top-right" />
      <Router>
        <Routes>
          {/* Public marketing landing page — content controlled by Super Admin */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
          <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
          <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
          <Route path="/reset-password" element={<PublicRoute><ResetPassword /></PublicRoute>} />
          <Route path="/privacy-policy" element={<LegalPage />} />
          <Route path="/terms-of-service" element={<LegalPage />} />
          <Route path="/security" element={<LegalPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/plans" element={<PlansPage />} />
          <Route path="/super-admin/login" element={<PublicRoute><SuperAdminLogin /></PublicRoute>} />
          {/* Super Admin Portal — completely separate from company accounting */}
          <Route path="/super-admin" element={<SuperAdminRoute><SuperAdminLayout /></SuperAdminRoute>}>
            <Route index element={<SuperAdminDashboard />} />
            <Route path="companies" element={<SuperAdminCompanies />} />
            <Route path="companies/:id" element={<SuperAdminCompanyDetail />} />
            <Route path="subscriptions" element={<SuperAdminSubscriptions />} />
            <Route path="plans" element={<SuperAdminPlans />} />
            <Route path="payments" element={<SuperAdminPayments />} />
            <Route path="users" element={<SuperAdminUsers />} />
            <Route path="notifications" element={<SuperAdminNotifications />} />
            <Route path="activity" element={<SuperAdminActivity />} />
            <Route path="support" element={<SuperAdminSupport />} />
            <Route path="reports" element={<SuperAdminReports />} />
            <Route path="payments/:id" element={<SuperAdminPaymentPage />} />
            <Route path="invoices/:id" element={<SuperAdminInvoicePage />} />
            <Route path="legal" element={<SuperAdminLegal />} />
            <Route path="chat" element={<SuperAdminChat />} />
            <Route path="settings" element={<SuperAdminSettings />} />
            <Route path="landing" element={<SuperAdminLandingPage />} />
          </Route>
          {/* Company Accounting App — super admins are redirected away.
              NOTE: no index route here so the public LandingPage owns "/" */}
          <Route path="/" element={<PrivateRoute><AdminRedirect><Layout /></AdminRedirect></PrivateRoute>}>
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="accounts" element={<AccountList />} />
            <Route path="journal" element={<JournalList />} />
            <Route path="journal/new" element={<JournalEntry />} />
            <Route path="journal/:id" element={<JournalEntry />} />
            <Route path="customers" element={<CustomerList />} />
            <Route path="customers/new" element={<CustomerForm />} />
            <Route path="customers/:id" element={<CustomerForm />} />
            <Route path="vendors" element={<VendorList />} />
            <Route path="vendors/new" element={<VendorForm />} />
            <Route path="vendors/:id" element={<VendorForm />} />
            <Route path="invoices" element={<InvoiceList />} />
            <Route path="invoices/new" element={<InvoiceEditor />} />
            <Route path="invoices/:id" element={<InvoiceView />} />
            <Route path="invoices/:id/edit" element={<InvoiceForm />} />
            <Route path="bills" element={<BillList />} />
            <Route path="bills/new" element={<BillForm />} />
            <Route path="bills/:id" element={<BillForm />} />
            <Route path="payments" element={<PaymentList />} />
            <Route path="payments/new" element={<PaymentForm />} />
            <Route path="payments/:id/edit" element={<PaymentForm />} />
            <Route path="payments/:id" element={<PaymentView />} />
            <Route path="banking" element={<BankAccounts />} />
            <Route path="banking/transactions" element={<BankTransactions />} />
            <Route path="banking/reconciliation" element={<BankReconciliation />} />
            <Route path="banking/write-check" element={<WriteCheck />} />
            <Route path="banking/deposit" element={<RecordDeposit />} />
            <Route path="expenses" element={<ExpenseList />} />
            <Route path="expenses/new" element={<ExpenseForm />} />
            <Route path="products" element={<ProductList />} />
            <Route path="products/new" element={<ProductForm />} />
            <Route path="products/:id" element={<ProductForm />} />
            <Route path="inventory" element={<InventoryList />} />
            <Route path="inventory/adjust" element={<InventoryAdjust />} />
            <Route path="assets" element={<AssetList />} />
            <Route path="assets/new" element={<AssetForm />} />
            <Route path="assets/:id" element={<AssetForm />} />
            <Route path="reports" element={<ReportCenter />} />
            <Route path="estimates" element={<Estimates />} />
            <Route path="estimates/new" element={<EstimateForm />} />
            <Route path="estimates/:id/edit" element={<EstimateForm />} />
            <Route path="sales-orders" element={<SalesOrders />} />
            <Route path="sales-orders/new" element={<SalesOrderForm />} />
            <Route path="sales-orders/:id/edit" element={<SalesOrderForm />} />
            <Route path="purchase-orders" element={<PurchaseOrders />} />
            <Route path="purchase-orders/new" element={<PurchaseOrderForm />} />
            <Route path="purchase-orders/:id/edit" element={<PurchaseOrderForm />} />
            <Route path="employees" element={<Employees />} />
            <Route path="employees/new" element={<EmployeeForm />} />
            <Route path="employees/:id/edit" element={<EmployeeForm />} />
            <Route path="employees/:id/payslip" element={<PaySlip />} />
            <Route path="payslip" element={<PaySlip />} />
            <Route path="payroll" element={<Payroll />} />
            <Route path="documents" element={<Documents />} />
            <Route path="applications" element={<Applications />} />
            <Route path="services" element={<Services />} />
            <Route path="notifications" element={<Notifications />} />
            <Route path="saas" element={<SaaSDashboard />} />
            <Route path="tax" element={<TaxSettings />} />
            <Route path="settings/fiscal" element={<FiscalManagement />} />
            <Route path="settings/audit" element={<AuditTrail />} />
            <Route path="settings/users" element={<UserManagement />} />
            <Route path="settings/company" element={<CompanySettings />} />
            <Route path="settings/currency" element={<CurrencySettings />} />
            <Route path="settings/branding" element={<ThemeBrandingSettings />} />
            <Route path="settings/profile" element={<ProfileSettings />} />
            <Route path="subscription" element={<SubscriptionPage />} />
            <Route path="subscription/renew" element={<SubscriptionRenewalPage />} />
            <Route path="recurring" element={<RecurringSchedules />} />
            <Route path="settings/backup" element={<BackupManager />} />
          </Route>
        </Routes>
      </Router>
      </BrandProvider>
    </AuthProvider>
  );
}

export default App;
